import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pedService } from '../../services/endpoints/pedService';
import { PEDCompound, PEDCycle, CompoundType } from '../../types';
import { format } from 'date-fns';

const COMPOUND_GROUP_LABELS: Record<CompoundType, string> = {
  [CompoundType.InjectableAAS]: '💉 Injectable AAS',
  [CompoundType.OralAAS]:       '💊 Oral AAS',
  [CompoundType.Ancillary]:     '🛡️ Ancillaries',
  [CompoundType.Peptide]:       '🔬 Peptides',
};

const COMPOUND_TYPE_ORDER: CompoundType[] = [
  CompoundType.InjectableAAS,
  CompoundType.OralAAS,
  CompoundType.Ancillary,
  CompoundType.Peptide,
];

interface CompoundEntry {
  logId?: string;       // present when editing an existing logged entry
  compoundId: string;
  doseAmount: number | '';
  notes: string;
}

export interface LogFormModalProps {
  compounds: PEDCompound[];
  activeCycle?: PEDCycle | null;
  editLog?: any;
  /** Pre-select a specific date when opening in add/edit-date mode (no editLog) */
  initialDate?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyEntry = (): CompoundEntry => ({ compoundId: '', doseAmount: '', notes: '' });

/** Daily dose = weeklyTotal / 7, rounded to 1 decimal place */
const dailyDose = (weeklyTotal: number): number =>
  Math.round((weeklyTotal / 7) * 10) / 10;

/** Shared grouped compound <select> */
const CompoundSelect: React.FC<{
  compounds: PEDCompound[];
  value: string;
  onChange: (v: string) => void;
  className?: string;
}> = ({ compounds, value, onChange, className = '' }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className={`bg-white border border-dark-500 text-black rounded-lg px-3 py-2 ${className}`}
    required
  >
    <option value="">Select compound…</option>
    {COMPOUND_TYPE_ORDER.map(type => {
      const group = compounds.filter(c => c.type === type);
      if (group.length === 0) return null;
      return (
        <optgroup key={type} label={COMPOUND_GROUP_LABELS[type]}>
          {group.map(c => (
            <option key={c.compoundId} value={c.compoundId}>
              {c.name} ({c.unit})
            </option>
          ))}
        </optgroup>
      );
    })}
  </select>
);

const LogFormModal: React.FC<LogFormModalProps> = ({ compounds, activeCycle, editLog, initialDate, onClose, onSuccess }) => {
  const isEditing = !!editLog;   // true when editing a single log from the history list
  const queryClient = useQueryClient();
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  // ── Edit-mode state (single-entry edit via editLog prop — unchanged) ──────
  const [editData, setEditData] = useState(() => {
    if (editLog) {
      return {
        date: editLog.date ? editLog.date.split('T')[0] : format(new Date(), 'yyyy-MM-dd'),
        compoundId: editLog.compoundId || editLog.compound?.compoundId || '',
        doseAmount: editLog.doseAmount ?? 0,
        notes: editLog.notes || '',
      };
    }
    return { date: format(new Date(), 'yyyy-MM-dd'), compoundId: '', doseAmount: 0, notes: '' };
  });

  // ── Add-mode date ─────────────────────────────────────────────────────────
  const [date, setDate] = useState(initialDate ?? format(new Date(), 'yyyy-MM-dd'));

  // ── Cycle defaults (memoised — only changes if activeCycle changes) ───────
  const cycleEntries: CompoundEntry[] = useMemo(() => {
    if (isEditing || !activeCycle?.details?.length) return [];
    return activeCycle.details
      .filter(d => d.compound?.compoundId)
      .map(d => ({
        compoundId: d.compound!.compoundId,
        doseAmount: dailyDose(d.weeklyTotal ?? 0),
        notes: '',
      }));
  }, [activeCycle, isEditing]);

  // ── Query: fetch existing logs for the selected date (add mode only) ──────
  const { data: existingLogsForDate = [], isLoading: isLoadingDate } = useQuery({
    queryKey: ['ped-logs-for-date', date],
    queryFn: () => pedService.getLogsByDate(date),
    enabled: !isEditing,
  });

  // True when the currently-selected date already has logs in the DB
  const isEditingDate = !isEditing && existingLogsForDate.length > 0;

  // ── Entries state ─────────────────────────────────────────────────────────
  const [entries, setEntries] = useState<CompoundEntry[]>(
    cycleEntries.length > 0 ? cycleEntries : [emptyEntry()]
  );

  // Re-populate entries whenever the date or its existing logs change
  useEffect(() => {
    if (isEditing) return;
    if (existingLogsForDate.length > 0) {
      // Existing logs for this date → load them as editable rows
      setEntries(
        existingLogsForDate.map((log: any) => ({
          logId: log.logId,
          compoundId: log.compound?.compoundId || log.compoundId || '',
          doseAmount: log.doseAmount ?? '',
          notes: log.notes || '',
        }))
      );
    } else {
      // No logs for this date → fall back to cycle defaults (or blank)
      setEntries(cycleEntries.length > 0 ? cycleEntries : [emptyEntry()]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLogsForDate]);

  // fromCycle banner shown only when no existing logs and cycle defaults are loaded
  const fromCycle = !isEditing && !isEditingDate && cycleEntries.length > 0;

  const updateEntry = (idx: number, patch: Partial<CompoundEntry>) =>
    setEntries(prev => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  const addEntry    = () => setEntries(prev => [...prev, emptyEntry()]);
  const removeEntry = (idx: number) => setEntries(prev => prev.filter((_, i) => i !== idx));

  // ── Single-log edit mutation (unchanged) ─────────────────────────────────
  const updateLogMutation = useMutation({
    mutationFn: (data: any) => pedService.updateLog(editLog.logId, data),
    onSuccess: () => {
      setStatusMessage({ type: 'success', text: 'Dose updated successfully!' });
      setTimeout(() => onSuccess(), 1500);
    },
    onError: () => setStatusMessage({ type: 'error', text: 'Failed to update dose. Please try again.' }),
  });

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Single-log edit (editLog prop mode)
    if (isEditing) {
      updateLogMutation.mutate(editData);
      return;
    }

    // Validate
    const invalid = entries.some(e => !e.compoundId || e.doseAmount === '' || Number(e.doseAmount) <= 0);
    if (invalid) {
      setStatusMessage({ type: 'error', text: 'Please select a compound and enter a valid dose for every row.' });
      return;
    }

    setBulkSaving(true);
    setStatusMessage(null);
    try {
      if (isEditingDate) {
        // Update existing logs for this date (may include new rows added by user)
        await Promise.all(
          entries.map(entry =>
            entry.logId
              ? pedService.updateLog(entry.logId, {
                  date,
                  compoundId: entry.compoundId,
                  doseAmount: Number(entry.doseAmount),
                  notes: entry.notes,
                })
              : pedService.createLog({
                  date,
                  compoundId: entry.compoundId,
                  doseAmount: Number(entry.doseAmount),
                  notes: entry.notes,
                } as any)
          )
        );
        const n = entries.length;
        setStatusMessage({ type: 'success', text: `${n} dose${n > 1 ? 's' : ''} updated successfully!` });
      } else {
        // Create new logs
        await Promise.all(
          entries.map(entry =>
            pedService.createLog({
              date,
              compoundId: entry.compoundId,
              doseAmount: Number(entry.doseAmount),
              notes: entry.notes,
            } as any)
          )
        );
        const n = entries.length;
        setStatusMessage({ type: 'success', text: `${n} dose${n > 1 ? 's' : ''} logged successfully!` });
      }
      // Refresh background data without closing the modal
      queryClient.invalidateQueries({ queryKey: ['ped-logs'] });
      queryClient.invalidateQueries({ queryKey: ['ped-logs-recent'] });
      queryClient.invalidateQueries({ queryKey: ['ped-logs-for-date', date] });
      queryClient.invalidateQueries({ queryKey: ['analytics-ped-logs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      // Advance date by 1 day so the user is ready to log the next day immediately
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      setDate(format(nextDay, 'yyyy-MM-dd'));
      // Auto-clear the success banner after 3 s
      setTimeout(() => setStatusMessage(null), 3000);
    } catch {
      setStatusMessage({ type: 'error', text: 'One or more doses failed to save. Please try again.' });
    } finally {
      setBulkSaving(false);
    }
  };

  const isSaving = bulkSaving || updateLogMutation.isPending;

  // Dynamic header / button label
  const headerTitle = isEditing
    ? 'Edit Dose'
    : isEditingDate
    ? 'Edit Doses'
    : 'Log Doses';

  const submitLabel = isSaving
    ? 'Saving…'
    : isEditing || isEditingDate
    ? entries.length > 1 ? `Update ${entries.length} Doses` : 'Update Dose'
    : entries.length > 1 ? `Log ${entries.length} Doses` : 'Log Dose';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* ── Header ── */}
        <div className="p-6 border-b border-dark-700 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-bold text-gold-500">{headerTitle}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* ── Body ── */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
            <input
              type="date"
              value={isEditing ? editData.date : date}
              onChange={e => isEditing
                ? setEditData({ ...editData, date: e.target.value })
                : setDate(e.target.value)
              }
              className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
              required
            />
            {/* Loading indicator while fetching logs for the new date */}
            {!isEditing && isLoadingDate && (
              <p className="text-xs text-gray-400 mt-1 animate-pulse">Checking for existing logs…</p>
            )}
          </div>

          {isEditing ? (
            /* ── Single-log edit (via editLog prop) — unchanged ─────────── */
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Compound</label>
                <CompoundSelect
                  compounds={compounds}
                  value={editData.compoundId}
                  onChange={v => setEditData({ ...editData, compoundId: v })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Dose Amount</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={editData.doseAmount}
                    onChange={e => setEditData({ ...editData, doseAmount: parseFloat(e.target.value) })}
                    className="bg-white border border-dark-500 text-black rounded-lg px-4 py-2 flex-1"
                    required
                  />
                  <div className="bg-dark-700 border border-dark-600 rounded-lg px-4 py-2 text-gray-400 flex items-center">
                    {editData.compoundId ? compounds.find(c => c.compoundId === editData.compoundId)?.unit : 'mg'}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes (Optional)</label>
                <textarea
                  value={editData.notes}
                  onChange={e => setEditData({ ...editData, notes: e.target.value })}
                  rows={2}
                  className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
                  placeholder="e.g., Morning injection, left glute"
                />
              </div>
            </>
          ) : (
            /* ── Multi-compound rows (add or date-edit mode) ─────────────── */
            <>
              {/* Status banner */}
              {isEditingDate ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-400">
                  <span>✏️</span>
                  <span>
                    <strong>{existingLogsForDate.length} dose{existingLogsForDate.length > 1 ? 's' : ''}</strong>
                    {' '}already logged for this date — editing existing entries
                  </span>
                </div>
              ) : fromCycle ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-gold-500/10 border border-gold-500/30 rounded-lg text-xs text-gold-400">
                  <span>⚡</span>
                  <span>Pre-populated from <strong>{activeCycle!.name}</strong> · adjust doses or notes as needed</span>
                </div>
              ) : null}

              <div className="space-y-3">
                {entries.map((entry, idx) => {
                  const unit = entry.compoundId
                    ? compounds.find(c => c.compoundId === entry.compoundId)?.unit ?? 'mg'
                    : 'mg';
                  return (
                    <div key={idx} className="bg-dark-700/50 border border-dark-600/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Compound {idx + 1}
                        </span>
                        {entries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEntry(idx)}
                            className="text-red-400 hover:text-red-300 text-xs"
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>

                      <div className="flex gap-2 items-center">
                        <CompoundSelect
                          compounds={compounds}
                          value={entry.compoundId}
                          onChange={v => updateEntry(idx, { compoundId: v })}
                          className="flex-1 min-w-0"
                        />
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={entry.doseAmount}
                          onChange={e => updateEntry(idx, {
                            doseAmount: e.target.value === '' ? '' : parseFloat(e.target.value),
                          })}
                          className="bg-white border border-dark-500 text-black rounded-lg px-3 py-2 w-24 text-sm"
                          placeholder="Dose"
                          required
                        />
                        <span className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-gray-400 text-sm w-14 text-center flex-shrink-0">
                          {unit}
                        </span>
                      </div>

                      <input
                        type="text"
                        value={entry.notes}
                        onChange={e => updateEntry(idx, { notes: e.target.value })}
                        className="bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 w-full text-sm"
                        placeholder="Notes (optional) — e.g., left glute"
                      />
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addEntry}
                className="w-full py-2 border border-dashed border-gray-600 text-gray-400 hover:text-gold-400 hover:border-gold-500/50 rounded-lg text-sm transition-colors"
              >
                + Add Another Compound
              </button>
            </>
          )}

          {/* ── Footer — confirmation banner below buttons for mobile visibility ── */}
          <div className="space-y-3 pt-2">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-dark-700"
              >
                {isEditing ? 'Cancel' : 'Close'}
              </button>
              <button
                type="submit"
                disabled={isSaving || isLoadingDate}
                className="px-6 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400 disabled:opacity-50 min-w-[140px]"
              >
                {submitLabel}
              </button>
            </div>
            {statusMessage && (
              <div className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                statusMessage.type === 'success'
                  ? 'bg-green-600/20 border border-green-600/30 text-green-400'
                  : 'bg-red-600/20 border border-red-600/30 text-red-400'
              }`}>
                <span>{statusMessage.type === 'success' ? '✅' : '❌'}</span>
                {statusMessage.text}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogFormModal;
